import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Turnstile } from "@marsidev/react-turnstile";
import { MDCSnackbar } from '@material/snackbar';
import { getConfigDeviceAndLocationData } from '../common/configDeviceAndLocationData';
import SubscriptionService from '../services/subscriptionService';
import CircularProgress from './circularProgress';
import { UnsubscribeDescripition } from './UnsubscribeDescription';

const excludeRedirects = ['v5agj3-testvendor.onlive.site', 'meet.onlive.site'];

const Unsubscribe = ({ data, subscription, config, channel, channelURL, snackbarDomRef }) => {
    const { t } = useTranslation();
    const [unsubscribePending, setUnsubscribePending] = useState(false);
    const [unsubscribeSuccess, setUnsubscribeSuccess] = useState(false);
    const [userToken, setUserToken] = useState(null);
    const snackbarRef = useRef(null);

    const enqueueSnackbar = useCallback((message, timeout) => {
        if (snackbarRef.current) {
            snackbarRef.current.timeoutMs = timeout || 4000;
            snackbarRef.current.labelText = message;
            snackbarRef.current.open();
        }
    }, []);

    useEffect(() => {
        if (!snackbarRef.current && snackbarDomRef.current) {
            snackbarRef.current = new MDCSnackbar(snackbarDomRef.current);
            snackbarRef.current.progress = 0.5;
            snackbarRef.current.determinate = false;
        }
        getConfigDeviceAndLocationData();
    }, [snackbarDomRef]);

    const handleUnsubscribe = useCallback(() => {
        setUnsubscribePending(true);
        getConfigDeviceAndLocationData().then((deviceAndLocationData) => {
            SubscriptionService.cancel(data.token, { ...data, deviceAndLocationData }, { ['X-Onlive-Captcha-Token']: userToken }).then((response) => {
                setUnsubscribePending(false);
                setUnsubscribeSuccess(true);
            }).catch((err) => {
                enqueueSnackbar(t("home.unsubscribe.error"));
                setUnsubscribePending(false);
            });
        });
    }, [enqueueSnackbar, data, t, userToken]);

    const shouldRedirectAfterCancel = (channelURL) => {
        if(!channelURL) {
            return false;
        }
        let domain = (new URL(channelURL)).hostname.replace('www.', '');
        return !excludeRedirects.includes(domain);
    }

    useEffect(() => {
        let redirectTimeout = null;
        if (unsubscribeSuccess && channelURL) {
            if(shouldRedirectAfterCancel(channelURL)) {
                redirectTimeout = setTimeout(() => {
                    window.location.replace(channelURL);
                }, 3000);
            }
        }

        return () => {
            if (redirectTimeout) {
                clearTimeout(redirectTimeout);
            }
        };
    }, [unsubscribeSuccess, channelURL]);

    return (

        <div className="content full-height flex-center flex-wrap flex-orientation-v">
            <div className="organization-logo">
                <img src={config.logo} height="100%" alt={"logo"} />
            </div>
            {!unsubscribeSuccess && <div className={'full-width'}>
                <UnsubscribeDescripition
                    data={data}
                    subscription={subscription}
                    config={config}
                    channel={channel}
                />

                <div className="full-width">
                    <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SIT_KEY}
                        onError={() => setUserToken(null)}
                        onExpire={() => setUserToken(null)}
                        onSuccess={setUserToken}
                    />
                </div>

                {unsubscribePending && <div className="full-width">
                    <CircularProgress />
                </div>}


                {!unsubscribePending && <div className="unsubscribe-button-container">
                    <button
                        disabled={unsubscribePending || !userToken}
                        className="mdc-button mdc-button--raised"
                        onClick={handleUnsubscribe}
                    >
                        <div className="mdc-button__ripple"></div>
                        <span className="mdc-button__label fs-14 bold">
                            {t("home.unsubscribe")}
                        </span>
                    </button>
                </div>}
            </div>}
            {unsubscribeSuccess && <div className="pr-1rem pl-1rem full-width">
                <p className="mdc-typography mdc-typography--subtitle1 fs-20">
                    <span>{t("home.unsubscribe.success")}</span>
                </p>
            </div>}
            {unsubscribeSuccess && channelURL && shouldRedirectAfterCancel(channelURL) && <div className="full-width flex-left">
                <CircularProgress /> <span className="mdc-typography mdc-typography--subtitle1 fs-20 ml-1rem"> {t("home.redirecting")}</span>
            </div>}
        </div>
    );
}

export default Unsubscribe;
